/**
 * Centralized motion transitions, durations, and choreography ratios.
 *
 * All motion components and imperative `animate()` calls in the SMB action-card
 * system should consume these tokens rather than inlining stiffness/damping or
 * duration values. Tune here to retune the entire animation feel.
 *
 * Pair with `<MotionConfig reducedMotion="user">` at the route root, which
 * makes declarative animations honor OS prefers-reduced-motion automatically;
 * imperative `animate()` calls still need their own `useReducedMotion` checks.
 */

const cardEase = [0.42, 0, 1, 1] as const;

/**
 * Tween durations (in seconds). Exposed as numbers so callers that scale them
 * (e.g. Swipable scales by `commitProgress`) can multiply directly.
 */
export const motionDurations = {
  /** Base duration for Swipable's pre-commit + finish-exit tweens. */
  cardExitS: 0.24,
  /** Duration of the card deck's stack skip-peel exit. */
  skipOutS: 0.28,
  /** Duration of the card deck's fade exit (grid skip / completion). */
  cardFadeS: 0.2,
  /** Duration of the ActionCard front/back flip. */
  flipS: 0.5,
} as const;

/**
 * Fraction of Swipable's swipe-out tween that plays before the swipe handler
 * is invoked. The card visibly pauses at this fraction of the exit during
 * any async work the handler performs (e.g. submitting an answer), then
 * finishes the remaining motion on resolve.
 */
export const commitProgressDefault = 0.72;

export const motionTransitions = {
  /** Layout/grid spring used by the card deck for idle layout shifts. */
  cardLayoutSpring: { type: "spring", stiffness: 380, damping: 34, mass: 0.8 },
  /** Spring used by Swipable.reset to return the card to origin. */
  cardSwipeReset: { type: "spring", stiffness: 420, damping: 34, mass: 0.8 },
  /** Tween used by the card deck's stack skip-peel exit. */
  skipOut: { type: "tween", duration: motionDurations.skipOutS, ease: cardEase },
  /** Tween used by the card deck's fade exits (grid skip / completion). */
  cardFade: { type: "tween", duration: motionDurations.cardFadeS, ease: cardEase },
  /** Tween used by ActionCard's flip animation. */
  flip: { type: "tween", duration: motionDurations.flipS, ease: "easeOut" },
} as const;

export const cardExitEase = cardEase;
